import React, { useState, useEffect, useRef } from 'react';
import type { StockRequestLine } from '../../../store/stockRequestStore';
import type { Item } from '../../../store/inventoryStore';

interface Theme {
  muted: string;
  text: string;
  inputBg: string;
  border: string;
  cardBg: string;
}

interface Props {
  line: StockRequestLine;
  items: Item[];
  colSpan?: number;
  responderRole: 'requester' | 'manager';
  proposerRole: 'requester' | 'manager';
  theme: Theme;
  isDark: boolean;
  inputCls: string;
  saving: boolean;
  onAccept: () => void;
  onDecline: () => void;
  onCounter: (substituteItemId: string) => void;
}

const NegotiationLinePanel: React.FC<Props> = ({
  line,
  items,
  colSpan = 6,
  responderRole,
  proposerRole,
  theme,
  isDark,
  inputCls,
  saving,
  onAccept,
  onDecline,
  onCounter,
}) => {
  const [showCounter, setShowCounter] = useState(false);
  const [counterItemId, setCounterItemId] = useState('');
  const [confirmDecline, setConfirmDecline] = useState(false);
  const declineBtnRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!confirmDecline) return;
    const handlePointerDown = (e: MouseEvent | TouchEvent) => {
      const target = e.target as Node | null;
      if (target && declineBtnRef.current?.contains(target)) return;
      setConfirmDecline(false);
    };
    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('touchstart', handlePointerDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('touchstart', handlePointerDown);
    };
  }, [confirmDecline]);

  const proposerLabel = proposerRole === 'manager' ? 'Manager' : 'Requester';
  const substituteName = line.substituteItemId
    ? items.find(i => i.id === line.substituteItemId)?.name ?? 'an alternative item'
    : null;
  const offerQty = line.approvedQty ?? line.requestedQty;

  return (
    <tr style={{ backgroundColor: isDark ? '#1a2540' : '#eff6ff' }}>
      <td colSpan={colSpan} className="pl-8 pr-3 py-2 space-y-2">
        <div className="flex items-center justify-between gap-2">
          <div className="text-sm" style={{ color: theme.muted }}>
            {line.substituteItemId ? (
              <>
                <span className="font-medium" style={{ color: isDark ? '#93c5fd' : '#1d4ed8' }}>
                  Current Substitute Offered
                </span>
                {' '}
                <span style={{ color: theme.text }}>
                  {offerQty}x <span className="font-bold">{substituteName}</span>
                </span>
              </>
            ) : (
              <>
                <span className="font-medium" style={{ color: isDark ? '#93c5fd' : '#1d4ed8' }}>
                  Unavailable proposed
                </span>
                {' — '}{proposerLabel} marked this item as unavailable
                {line.approvalReason ? <> ({line.approvalReason})</> : null}.
                {responderRole === 'requester' ? ' Do you accept, change, or decline?' : ' Your response:'}
              </>
            )}
          </div>
          <div className="flex items-center gap-1.5 flex-shrink-0">
            <button
              onClick={() => { setConfirmDecline(false); onAccept(); }}
              disabled={saving}
              className="text-[11px] px-2 py-1 rounded bg-green-600 hover:bg-green-700 text-white transition-colors font-medium disabled:opacity-50"
            >
              Accept
            </button>
            <button
              onClick={() => {
                setConfirmDecline(false);
                setShowCounter(v => !v);
              }}
              disabled={saving}
              className="text-[11px] px-2 py-1 rounded transition-colors font-medium disabled:opacity-50"
              style={{ backgroundColor: isDark ? '#1e3a5f' : '#dbeafe', color: isDark ? '#93c5fd' : '#1d4ed8' }}
            >
              Change
            </button>
            <button
              ref={declineBtnRef}
              onClick={() => {
                if (confirmDecline) {
                  setConfirmDecline(false);
                  onDecline();
                } else {
                  setShowCounter(false);
                  setConfirmDecline(true);
                }
              }}
              disabled={saving}
              className="text-[11px] px-2 py-1 rounded transition-colors font-medium disabled:opacity-50"
              style={
                confirmDecline
                  ? { backgroundColor: isDark ? '#7f1d1d' : '#fecaca', color: isDark ? '#fca5a5' : '#b91c1c' }
                  : { backgroundColor: isDark ? '#2b3140' : '#e5e7eb', color: theme.muted }
              }
            >
              {confirmDecline ? 'Sure?' : 'Decline'}
            </button>
          </div>
        </div>
        {showCounter && (
          <div className="flex items-end gap-2 pt-1">
            <div className="flex-1">
              <label className="text-xs block mb-1" style={{ color: theme.muted }}>
                Substitute Suggestion:
              </label>
              <select
                value={counterItemId}
                onChange={e => setCounterItemId(e.target.value)}
                className={inputCls}
                style={{ backgroundColor: theme.cardBg, borderColor: theme.border, color: theme.text }}
              >
                <option value="">Select substitute…</option>
                {items
                  .filter(i => i.id !== line.itemId)
                  .map(i => (
                    <option key={i.id} value={i.id}>{i.name}</option>
                  ))}
              </select>
            </div>
            <button
              type="button"
              disabled={saving || !counterItemId}
              onClick={() => {
                onCounter(counterItemId);
                setShowCounter(false);
                setCounterItemId('');
              }}
              className="inline-flex items-center justify-center h-7 px-2.5 rounded text-xs font-medium transition-colors whitespace-nowrap border disabled:opacity-50"
              style={
                counterItemId && !saving
                  ? { backgroundColor: '#16a34a', borderColor: '#16a34a', color: '#ffffff' }
                  : {
                      backgroundColor: isDark ? '#374151' : '#e5e7eb',
                      borderColor: isDark ? '#4b5563' : '#d1d5db',
                      color: isDark ? '#9ca3af' : '#6b7280',
                    }
              }
            >
              Send for Review
            </button>
          </div>
        )}
      </td>
    </tr>
  );
};

export default NegotiationLinePanel;
